import React, { useState } from 'react';
import { Briefcase, GraduationCap, FileDown, Mail, ChevronDown, Send } from 'lucide-react';
import Reveal from '../components/Reveal';
import { CandidatureForm } from '../components/CandidatureForm';
import { jobs, aboutForCandidates } from '../data/jobs';
import { contactInfo } from '../data/mock';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const icons = { mandataire: Briefcase, stage: GraduationCap };

const Bullets = ({ items }) => (
  <ul className="mt-4 space-y-2">
    {items.map((it) => <li key={it} className="flex items-start gap-3 text-[#010c23]/75 leading-relaxed"><span className="mt-[11px] h-1.5 w-1.5 flex-shrink-0 bg-[#ff7300]" />{it}</li>)}
  </ul>
);

const Cards = ({ cards }) => (
  <div className="mt-5 grid sm:grid-cols-2 gap-4 no-justify">
    {cards.map((c) => (
      <div key={c.title} className="bg-white border border-black/5 p-5">
        <h4 className="font-serif text-lg text-[#010c23]">{c.title}</h4>
        <p className="mt-2 text-sm text-[#010c23]/70 leading-relaxed">{c.text}</p>
      </div>
    ))}
  </div>
);

const JobSection = ({ section }) => (
  <div className="mt-10">
    <span className="block h-px w-12 bg-[#ff7300] mb-3" />
    <h3 className="font-serif text-2xl text-[#010c23] font-light">{section.title}</h3>
    {section.text && <p className="mt-3 text-[#010c23]/75 leading-relaxed">{section.text}</p>}
    {section.items && <Bullets items={section.items} />}
    {section.cards && <Cards cards={section.cards} />}
    {section.tags && (
      <div className="mt-4 flex flex-wrap gap-2">
        {section.tags.map((t) => <span key={t} className="px-3 py-1.5 text-xs tracking-wider uppercase bg-[#010c23] text-white">✓ {t}</span>)}
      </div>
    )}
  </div>
);

const JobActions = ({ job, onApply }) => (
  <div className="flex flex-wrap gap-4">
    <button type="button" onClick={onApply} className="btn-primary" data-testid={`job-apply-form-${job.id}`}><Send size={16} /> Postuler en ligne</button>
    <a href={`mailto:${contactInfo.email}?subject=${encodeURIComponent(job.mailSubject)}`} className="btn-outline-dark" data-testid={`job-apply-${job.id}`}><Mail size={16} /> Candidater par email</a>
    <a href={`${API}/documents/${job.pdf}`} className="btn-outline-dark" data-testid={`job-pdf-${job.id}`}><FileDown size={16} /> Télécharger l’offre (PDF)</a>
  </div>
);

const JobHeader = ({ job }) => {
  const Icon = icons[job.id];
  return (
    <div className="flex items-start gap-5">
      <span className="w-12 h-12 flex-shrink-0 flex items-center justify-center bg-[#010c23] text-[#ff7300]"><Icon size={22} /></span>
      <div>
        <p className="text-[11px] tracking-[0.35em] uppercase text-[#ff7300] font-medium">{job.badge}</p>
        <h2 className="font-serif text-3xl md:text-4xl text-[#010c23] font-light leading-tight mt-2">{job.title}</h2>
      </div>
    </div>
  );
};

const JobFacts = ({ facts }) => (
  <dl className="mt-8 grid grid-cols-2 lg:grid-cols-4 gap-4 no-justify">
    {facts.map((f) => (
      <div key={f.label} className="bg-white border border-black/5 p-4">
        <dt className="text-[10px] uppercase tracking-widest text-[#010c23]/50">{f.label}</dt>
        <dd className="mt-1 font-medium text-[#010c23] text-sm">{f.value}</dd>
      </div>
    ))}
  </dl>
);

const JobDetail = ({ job, onApply }) => (
  <div className="px-8 md:px-10 pb-10 border-t border-black/5 justify-text animate-fade-in" data-testid={`job-detail-${job.id}`}>
    {job.sections.map((s) => <JobSection key={s.title} section={s} />)}
    <div className="mt-12 p-6 bg-[#010c23] text-white flex flex-col md:flex-row md:items-center justify-between gap-6">
      <div>
        <p className="text-[11px] tracking-[0.35em] uppercase text-[#ff7300] font-medium">Envie de nous rejoindre ?</p>
        <p className="mt-2 text-white/80 text-sm">{job.mailHint} <span className="text-white font-medium">{contactInfo.email}</span></p>
      </div>
      <div className="flex flex-wrap gap-3 flex-shrink-0">
        <button type="button" onClick={onApply} className="btn-primary" data-testid={`job-detail-apply-form-${job.id}`}><Send size={16} /> Postuler en ligne</button>
        <a href={`mailto:${contactInfo.email}?subject=${encodeURIComponent(job.mailSubject)}`} className="inline-flex items-center gap-2 px-6 py-3 text-sm font-medium border border-white/30 text-white hover:border-[#ff7300] hover:text-[#ff7300] transition-colors"><Mail size={16} /> Par email</a>
      </div>
    </div>
  </div>
);

const JobCard = ({ job, index, onApply }) => {
  const [open, setOpen] = useState(false);
  return (
    <Reveal delay={index * 100}>
      <article id={job.id} className="scroll-mt-32 bg-[#fbf9f5] border border-black/5 hover:border-[#ff7300]/40 transition-colors" data-testid={`job-${job.id}`}>
        <div className="p-8 md:p-10">
          <JobHeader job={job} />
          <p className="mt-6 text-[#010c23]/75 leading-relaxed max-w-3xl">{job.summary}</p>
          <JobFacts facts={job.facts} />
          <div className="mt-8 flex flex-wrap items-center justify-between gap-4">
            <JobActions job={job} onApply={onApply} />
            <button type="button" onClick={() => setOpen(!open)} aria-expanded={open} data-testid={`job-toggle-${job.id}`} className="inline-flex items-center gap-2 text-sm font-medium text-[#010c23] hover:text-[#ff7300] transition-colors">
              {open ? 'Masquer le détail' : 'Voir le détail de l’offre'} <ChevronDown size={16} className={`transition-transform duration-300 ${open ? 'rotate-180' : ''}`} />
            </button>
          </div>
        </div>
        {open && <JobDetail job={job} onApply={onApply} />}
      </article>
    </Reveal>
  );
};

const RecrutementHero = () => (
  <section className="pt-40 pb-20 bg-[#010c23] text-white">
    <div className="max-w-[1400px] mx-auto px-6 lg:px-12">
      <Reveal>
        <div className="flex items-center gap-3 mb-6">
          <span className="h-px w-14 bg-[#ff7300]" />
          <span className="text-[11px] tracking-[0.35em] uppercase text-[#ff7300] font-medium">Nous rejoindre</span>
        </div>
        <h1 className="font-serif text-5xl md:text-7xl font-light leading-tight max-w-4xl">Construire avec nous un conseil <span className="italic text-[#ff7300]">exigeant</span></h1>
        <div className="mt-8 max-w-3xl space-y-4 text-lg text-white/70 leading-relaxed">
          {aboutForCandidates.map((p) => <p key={p.slice(0, 30)}>{p}</p>)}
        </div>
        <div className="mt-10 flex flex-wrap gap-3">
          {jobs.map((j) => <a key={j.id} href={`#${j.id}`} className="px-5 py-2.5 text-xs tracking-wider uppercase font-medium border border-white/25 text-white/85 hover:border-[#ff7300] hover:text-[#ff7300] transition-colors" data-testid={`job-anchor-${j.id}`}>{j.title}</a>)}
        </div>
      </Reveal>
    </div>
  </section>
);

const CandidatureSection = ({ offer, onOfferChange }) => (
  <section id="candidature" className="py-20 md:py-24 bg-[#fbf9f5] scroll-mt-28" data-testid="candidature-section">
    <div className="max-w-[1100px] mx-auto px-6 lg:px-12 grid lg:grid-cols-12 gap-12">
      <Reveal className="lg:col-span-5">
        <div className="section-label mb-3"><span className="divider-gold"></span>Candidater</div>
        <h2 className="font-serif text-4xl text-[#010c23] font-light leading-tight">Déposez votre candidature</h2>
        <p className="mt-6 text-[#010c23]/70 leading-relaxed">Sélectionnez l’offre qui vous intéresse, ou adressez-nous une candidature spontanée si aucune ne correspond à votre profil. Nous étudions chaque candidature avec attention.</p>
        <p className="mt-4 text-[#010c23]/70 leading-relaxed">Vous préférez l’e-mail ? Écrivez-nous à <a href={`mailto:${contactInfo.email}`} className="font-medium text-[#010c23] hover:text-[#ff7300] transition-colors" data-testid="job-spontaneous">{contactInfo.email}</a>.</p>
      </Reveal>
      <Reveal className="lg:col-span-7" delay={120}>
        <CandidatureForm offer={offer} onOfferChange={onOfferChange} />
      </Reveal>
    </div>
  </section>
);

const Recrutement = () => {
  const [offer, setOffer] = useState('spontanee');
  const applyTo = (id) => () => {
    setOffer(id);
    document.getElementById('candidature')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };
  return (
    <div>
      <RecrutementHero />
      <section className="py-20 md:py-24 bg-white">
        <div className="max-w-[1100px] mx-auto px-6 lg:px-12 space-y-10">
          {jobs.map((job, i) => <JobCard key={job.id} job={job} index={i} onApply={applyTo(job.id)} />)}
        </div>
      </section>
      <CandidatureSection offer={offer} onOfferChange={setOffer} />
    </div>
  );
};

export default Recrutement;
